'use client'

import { ColumnDef } from '@tanstack/react-table'
import { Ellipsis } from 'lucide-react'
import * as React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header'
import { DataTableCheckbox } from '@/components/data-table/data-table-checkbox'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { PostPurchaseRuleItem } from '@/lib/api/postPurchaseRules'
import { DateDisplay } from '@/components/date-display'
import { useStoreNavigate } from '@/hooks/use-app-navigate'
import { PostPurchaseResourceBadge } from '@/components/post-purchase/postPurchaseResourceBadge'

interface GetPostPurchaseRulesTableColumnsProps {
	storeId: string
	onEditRule: (ruleId: string) => void
	onDeleteRule: (ruleId: string) => void
}

export const usePostPurchaseRulesTableColumns = ({
	storeId,
	onEditRule,
	onDeleteRule,
}: GetPostPurchaseRulesTableColumnsProps): ColumnDef<PostPurchaseRuleItem>[] => {
	const { t } = useTranslation('postPurchaseRules')

	return React.useMemo(
		() => [
			{
				id: 'select',
				header: ({ table }) => (
					<Checkbox
						checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
						onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
						aria-label={t('table.selectAll')}
						className="translate-y-0.5"
					/>
				),
				cell: ({ row }) => (
					<DataTableCheckbox row={row} />
				),
				enableSorting: false,
				enableHiding: false,
				size: 40,
			},
			{
				id: 'id',
				accessorKey: 'id',
				header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.id')} />,
				cell: ({ row }) => {
					const rule = row.original
					return (
						<PostPurchaseResourceBadge postPurchaseId={rule.id} />
					)
				},
            meta: { label: t('table.columns.id') },
            enableSorting: false,
        },
        {
            id: 'priority',
				accessorKey: 'priority',
				header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.priority')} />,
				cell: ({ row }) => {
					const rule = row.original
					if (rule.priority === null || rule.priority === undefined) {
						return <span className="text-muted-foreground text-sm">-</span>
					}
					return <Badge variant="secondary">{rule.priority}</Badge>
				},
				enableSorting: false,
				meta: { label: t('table.columns.priority') },
			},
			{
				id: 'offers',
				header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.offers')} />,
				cell: ({ row }) => {
					const rule = row.original
					const offerCount = rule.offers?.length || 0
					return (
						<div className="text-sm">
							<span className="font-medium">{offerCount}</span>
							<span className="text-muted-foreground ml-1">
								{offerCount === 1 ? t('table.offer') : t('table.offers')}
							</span>
						</div>
					)
				},
				enableSorting: false,
				meta: { label: t('table.columns.offers') },
			},
			{
				id: 'lines',
				header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.conditions')} />,
				cell: ({ row }) => {
					const rule = row.original
					const lineCount = rule.lines?.length || 0
					return (
						<div className="text-sm">
							<span className="font-medium">{lineCount}</span>
							<span className="text-muted-foreground ml-1">
								{lineCount === 1 ? t('table.condition') : t('table.conditions')}
							</span>
						</div>
					)
				},
				enableSorting: false,
				meta: { label: t('table.columns.conditions') },
			},
			{
				id: 'ruleOperator',
				accessorKey: 'ruleOperator',
				header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.operator')} />,
				cell: ({ row }) => {
					const rule = row.original
					return (
						<Badge variant="outline" className="font-mono">
							{rule.ruleOperator || 'OR'}
						</Badge>
					)
				},
				enableSorting: false,
				meta: { label: t('table.columns.operator') },
			},
			{
				id: 'createdAt',
				accessorKey: 'createdAt',
				header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.created')} />,
				cell: ({ row }) => (
					<DateDisplay
						value={row.original.createdAt}
						format="MMM dd, yyyy"
						className="text-sm text-muted-foreground"
						fallback={t('table.na')}
					/>
				),
				enableSorting: false,
				meta: { label: t('table.columns.created') },
			},
			{
				id: 'actions',
				cell: ({ row }) => {
					const rule = row.original
					const { storeRoutes } = useStoreNavigate()
					return (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									aria-label={t('table.openMenu')}
									variant="ghost"
									className="data-[state=open]:bg-muted flex h-8 w-8 p-0"
								>
									<Ellipsis className="h-4 w-4" aria-hidden="true" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" className="w-[160px]">
								<DropdownMenuItem onClick={() => onEditRule(rule.id)}>
									{t('actions.edit')}
								</DropdownMenuItem>
								<DropdownMenuItem asChild>
									<Link to={storeRoutes.POST_PURCHASE_EDIT(rule.id)}>{t('actions.viewDetails')}</Link>
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem
									onClick={() => onDeleteRule(rule.id)}
									className="text-destructive focus:text-destructive"
								>
									{t('actions.delete')}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					)
				},
				enableSorting: false,
				enableHiding: false,
				size: 40,
			},
		],
		[t, onEditRule, onDeleteRule]
	)
}

